import { useRef, useState } from "react";
import { cn } from "@/utils/cn";
import { formatBytes } from "@/utils/uploads";
import { useIntake } from "@/features/intake/IntakeContext";
import type { FileUploadStep as FileUploadStepConfig } from "@/types/offer";
import type { StepRenderProps } from "./types";

export function FileUploadStep({ step }: StepRenderProps<FileUploadStepConfig>) {
  const { uploadItems, addFiles, removeUpload } = useIntake();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const accept = step.allowedFileTypes?.join(",");
  const atLimit = step.maxFiles != null && uploadItems.length >= step.maxFiles;

  const onPick = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void addFiles(step, Array.from(files));
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      {step.orderedUploadHint ? (
        <div className="mb-4 rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3">
          <p className="text-sm font-semibold text-brand-700">{step.orderedUploadHint.title}</p>
          <ol className="mt-2 flex flex-col gap-1.5">
            {step.orderedUploadHint.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-ink-soft">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
      <div
        className={cn(
          "flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors",
          dragging ? "border-brand-500 bg-brand-50" : "border-slate-300 bg-slate-50",
          atLimit && "pointer-events-none opacity-60",
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          onPick(e.dataTransfer.files);
        }}
      >
        <p className="text-lg font-medium text-ink">Drag &amp; drop your files here</p>
        <p className="mt-1 text-sm text-ink-muted">
          {step.allowedFileTypes?.length
            ? `Accepted: ${step.allowedFileTypes.join(", ")}`
            : "Any file type"}
          {step.maxFileSizeMb ? ` · up to ${step.maxFileSizeMb} MB each` : ""}
          {step.maxFiles ? ` · max ${step.maxFiles} files` : ""}
        </p>
        <button
          type="button"
          className="r1-btn-secondary mt-4"
          onClick={() => inputRef.current?.click()}
          disabled={atLimit}
        >
          Choose files
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple={step.maxFiles !== 1}
          accept={accept}
          className="sr-only"
          aria-label="Upload files"
          onChange={(e) => onPick(e.target.files)}
        />
      </div>

      {uploadItems.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-2" aria-label="Uploaded files">
          {uploadItems.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-ink">{item.fileName}</span>
                  <span className="shrink-0 text-xs text-ink-muted">
                    {formatBytes(item.sizeBytes)}
                  </span>
                </div>
                {item.status === "uploading" ? (
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-brand-500 transition-all"
                      style={{ width: `${Math.round(item.progress * 100)}%` }}
                    />
                  </div>
                ) : (
                  <p
                    className={cn(
                      "mt-1 text-xs font-medium",
                      item.status === "done" ? "text-green-600" : "text-red-600",
                    )}
                  >
                    {item.status === "done" ? "Uploaded" : item.error ?? "Upload failed"}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="r1-btn-ghost px-2 py-1 text-sm"
                onClick={() => removeUpload(item.id)}
                aria-label={`Remove ${item.fileName}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
